import { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Camera, ImagePlus, ArrowLeft, Info } from "lucide-react";
import { toast } from "sonner";
import { trackEvent } from "@/App";

export default function CaptureScreen({ onImageCaptured }) {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [preview, setPreview] = useState(null);

  useEffect(() => { trackEvent("page_view", "capture"); }, []);

  const processFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please select an image file"); return; }
    const reader = new FileReader();
    reader.onload = (e) => { setPreview(e.target.result); onImageCaptured(e.target.result); trackEvent("action", "capture", { action: "image_selected" }); };
    reader.readAsDataURL(file);
  };

  const handleProceed = () => {
    if (!preview) { toast.error("Please capture or select an image first"); return; }
    navigate("/analyze");
  };

  return (
    <div data-testid="capture-screen" className="min-h-screen flex flex-col px-5 py-6 max-w-lg mx-auto">
      <div className="flex items-center gap-3 mb-8">
        <Button data-testid="back-to-landing-btn" variant="ghost" size="icon" onClick={() => navigate("/")}
          className="rounded-xl text-[#7a7894] hover:text-white hover:bg-[#2a273b]">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h2 className="text-lg font-semibold text-white">Capture Image</h2>
          <p className="text-xs text-[#5a5873]">Step 1 of 3</p>
        </div>
      </div>

      <Card data-testid="instruction-card" className="mb-6 p-4 rounded-2xl bg-[#232134] border-[#33304a]">
        <div className="flex gap-3">
          <div className="w-8 h-8 rounded-lg bg-[#e91e63]/10 flex items-center justify-center flex-shrink-0">
            <Info className="w-4 h-4 text-[#e91e63]" />
          </div>
          <div className="space-y-1">
            <p className="text-sm font-medium text-white">Capture Guidelines</p>
            <ul className="text-xs text-[#7a7894] space-y-1 leading-relaxed">
              <li>Place a known VITA shade tab next to the target tooth</li>
              <li>Keep both in the same focal plane with even lighting</li>
              <li>Avoid direct glare or harsh shadows on the tooth</li>
            </ul>
          </div>
        </div>
      </Card>

      <div className="flex-1 flex flex-col gap-4">
        {preview ? (
          <div data-testid="image-preview-container" className="animate-fade-in rounded-2xl overflow-hidden border border-[#33304a] bg-[#15132a]">
            <img data-testid="captured-image-preview" src={preview} alt="Captured tooth" className="w-full object-contain max-h-[50vh]" />
          </div>
        ) : (
          <div data-testid="capture-placeholder"
            className="flex-1 flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-[#33304a] bg-[#15132a] min-h-[40vh] p-8">
            <div className="w-16 h-16 rounded-2xl bg-[#232134] flex items-center justify-center mb-5">
              <Camera className="w-8 h-8 text-[#5a5873]" />
            </div>
            <p className="text-sm text-[#5a5873] text-center leading-relaxed">
              Take a photo of the tooth with a VITA shade tab, or select from gallery
            </p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Button data-testid="take-photo-btn" onClick={() => cameraInputRef.current?.click()}
            className="h-13 rounded-2xl bg-[#232134] text-white border border-[#33304a] hover:bg-[#2a273b] hover:border-[#e91e63]/30 transition-all" variant="ghost">
            <Camera className="w-4 h-4 mr-2 text-[#e91e63]" /><span className="text-sm font-medium">Take Photo</span>
          </Button>
          <Button data-testid="select-gallery-btn" onClick={() => fileInputRef.current?.click()}
            className="h-13 rounded-2xl bg-[#232134] text-white border border-[#33304a] hover:bg-[#2a273b] hover:border-[#2196f3]/30 transition-all" variant="ghost">
            <ImagePlus className="w-4 h-4 mr-2 text-[#2196f3]" /><span className="text-sm font-medium">Gallery</span>
          </Button>
        </div>

        <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={(e) => processFile(e.target.files?.[0])} className="hidden" data-testid="camera-input" />
        <input ref={fileInputRef} type="file" accept="image/*" onChange={(e) => processFile(e.target.files?.[0])} className="hidden" data-testid="gallery-input" />

        {preview && (
          <Button data-testid="proceed-to-analyze-btn" onClick={handleProceed}
            className="btn-gradient w-full h-14 text-base font-semibold rounded-2xl text-white border-0 shadow-lg shadow-[#e91e63]/20 mt-4">
            Proceed to Analysis
          </Button>
        )}
      </div>
    </div>
  );
}
